import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";

import { updatePickupStatus } from "@features/pickups/services/pickupService";
import { returnVoucherPDFService } from "../services/returnVoucherPDFService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@shared/components/ui/button";
import { toast } from "sonner";
import { Check, Settings, X, Download, Loader2, FileText } from "lucide-react";
import { useState } from "react";
import { Input } from "@shared/components/ui/input";
import { useTranslation } from "react-i18next";

const ReturnVoucherAction = ({
  returnVoucherId,
  voucher,
  showPDFActions = false,
  showUpdateStatus = false,
}) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [quantity, setQuantity] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (action) => updatePickupStatus(returnVoucherId, action),
  });

  const handleAction = async (action) => {
    const toastId = toast.loading(t("returnVoucher.processingVoucher"));
    mutate(action, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["returnVouchers"] });
        toast.success(t("returnVoucher.voucherUpdated"), { id: toastId });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || t("common.error"), {
          id: toastId,
        });
      },
    });
  };

  const handleGeneratePDF = async () => {
    try {
      setIsGenerating(true);
      await returnVoucherPDFService.generatePDF(returnVoucherId);
      toast.success(t("returnVoucher.pdf.pdfGenerated"));
      queryClient.invalidateQueries({ queryKey: ["returnVouchers"] });
    } catch (error) {
      toast.error(error.message || t("returnVoucher.pdf.error.generation"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsDownloading(true);
      await returnVoucherPDFService.downloadPDF(
        returnVoucherId,
        `bon-retour-${voucher?.ref || returnVoucherId}.pdf`
      );
      toast.success(t("returnVoucher.pdf.pdfDownloaded"));
    } catch (error) {
      toast.error(error.message || t("returnVoucher.pdf.error.download"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* PDF Actions - shown when showPDFActions is true */}
      {showPDFActions && (
        <>
          {/* Generate PDF Button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleGeneratePDF}
            disabled={isGenerating || isDownloading}
            className="flex items-center gap-1"
            title={t("returnVoucher.pdf.generatePDF")}
          >
            {isGenerating ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <FileText className="size-4" />
            )}
          </Button>

          {/* Download PDF Button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDownloadPDF}
            disabled={!voucher?.bonDeRetour || isGenerating || isDownloading}
            className={`flex items-center gap-1 ${
              !voucher?.bonDeRetour ? "opacity-50 cursor-not-allowed" : ""
            }`}
            title={t("returnVoucher.pdf.downloadPDF")}
          >
            {isDownloading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
          </Button>
        </>
      )}

      {/* Update Status Dialog - only shown when showUpdateStatus is true */}
      {showUpdateStatus && (
        <Dialog>
          <DialogTrigger asChild>
            <Button disabled={isPending} variant="ghost">
              <Settings className="size-4 text-blue-600 mx-1" />
              {t("common.actions")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("returnVoucher.action")}</DialogTitle>
              <DialogDescription>
                {t("returnVoucher.updateStatusDescription")}
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center space-x-2">
              <div className="grid flex-1 gap-2">
                <Label htmlFor="quantity">{t("common.quantity")}</Label>
                <Input
                  id="quantity"
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(e.target.value);
                  }}
                />
                <Label htmlFor="note">
                  {t("common.note")} ({t("common.optional")})
                </Label>

                <Textarea
                  id="note"
                  value={note}
                  placeholder={t("common.note") + " ..."}
                  onChange={(e) => {
                    setNote(e.target.value);
                  }}
                />
              </div>
            </div>

            <DialogFooter className="sm:justify-between">
              <DialogClose asChild>
                <Button type="button" size="sm" variant="secondary">
                  {t("common.close")}
                </Button>
              </DialogClose>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  size="sm"
                  className="bg-green-600 hover:bg-green-500"
                  disabled={isPending}
                  onClick={() =>
                    handleAction({
                      status: "approved",
                      statusNote: note,
                      quantity: parseInt(quantity),
                    })
                  }
                >
                  <Check className="mx-1 size-4" />
                  {t("common.approve")}
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    handleAction({
                      status: "rejected",
                      statusNote: note,
                    })
                  }
                  disabled={isPending}
                  variant="destructive"
                >
                  <X className="mx-1 size-4" />
                  {t("status.rejected")}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ReturnVoucherAction;
